import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dog, Plus, Edit2, Trash2, Camera, MapPin, Heart,
  Shield, Check, X, ChevronLeft, Settings, LogOut,
  Sparkles, Calendar, User, Loader2, Image
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import LanguageSwitcher from '../components/LanguageSwitcher';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Cloudinary upload helper
async function uploadToCloudinary(file, folder = 'puppoto/dogs') {
  const sigRes = await fetch(`${API_URL}/api/puppoto/cloudinary/signature?folder=${folder}`, {
    credentials: 'include'
  });
  
  if (!sigRes.ok) {
    throw new Error('Failed to get upload signature');
  }
  
  const sig = await sigRes.json();
  
  const form = new FormData();
  form.append('file', file);
  form.append('api_key', sig.api_key);
  form.append('timestamp', sig.timestamp);
  form.append('signature', sig.signature);
  form.append('folder', sig.folder);
  
  const uploadRes = await fetch(
    `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
    { method: 'POST', body: form }
  );
  
  if (!uploadRes.ok) {
    throw new Error('Upload failed');
  }
  
  return uploadRes.json();
}

// Dog breed suggestions
const BREEDS = [
  "Labrador Retriever", "Golden Retriever", "German Shepherd", "French Bulldog",
  "Bulldog", "Poodle", "Beagle", "Rottweiler", "Dachshund", "Yorkshire Terrier",
  "Boxer", "Cavalier King Charles Spaniel", "Miniature Schnauzer", "Shih Tzu",
  "Border Collie", "Australian Shepherd", "Pomeranian", "Siberian Husky",
  "Great Dane", "Doberman Pinscher", "Maltese", "Chihuahua", "Pug", "Corgi",
  "Cavoodle", "Groodle", "Labradoodle", "Bernedoodle", "Cockapoo", "Mixed Breed"
];

const PERSONALITIES = [
  "Playful", "Calm", "Energetic", "Friendly", "Shy", "Protective",
  "Cuddly", "Independent", "Loyal", "Smart", "Stubborn", "Gentle"
];

// Add Dog Modal Component
const AddDogModal = ({ isOpen, onClose, onSave, editDog = null }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: '',
    breed: '',
    age: '',
    gender: 'male',
    bio: '',
    looking_for: 'playdate',
    vaccinated: false,
    personality: [],
    photos: [],
    suburb: ''
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Handle photo upload
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    try {
      for (const file of files.slice(0, 5 - form.photos.length)) { // Max 5 photos
        const result = await uploadToCloudinary(file, 'puppoto/dogs');
        setForm(prev => ({ ...prev, photos: [...prev.photos, result.secure_url] }));
      }
      toast.success('Photo uploaded!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (index) => {
    setForm(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  useEffect(() => {
    if (editDog) {
      setForm({
        name: editDog.name || '',
        breed: editDog.breed || '',
        age: editDog.age?.toString() || '',
        gender: editDog.gender || 'male',
        bio: editDog.bio || '',
        looking_for: editDog.looking_for || 'playdate',
        vaccinated: editDog.vaccinated || false,
        personality: editDog.personality || [],
        photos: editDog.photos || [],
        suburb: editDog.location?.suburb || ''
      });
    }
  }, [editDog]);

  const togglePersonality = (trait) => {
    setForm(prev => ({
      ...prev,
      personality: prev.personality.includes(trait)
        ? prev.personality.filter(p => p !== trait)
        : [...prev.personality, trait].slice(0, 5) // Max 5 traits
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.breed) {
      toast.error('Name and breed are required');
      return;
    }

    setSaving(true);
    try {
      await onSave({
        ...form,
        age: parseInt(form.age) || 1
      });
      onClose();
      setForm({
        name: '', breed: '', age: '', gender: 'male', bio: '',
        looking_for: 'playdate', vaccinated: false, personality: [], photos: [], suburb: ''
      });
    } catch (error) {
      toast.error('Failed to save dog profile');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white rounded-3xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            {editDog ? 'Edit Dog Profile' : 'Add Your Dog'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-[#1A1817] mb-2">Dog's Name *</label>
            <Input
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Bella, Max, Luna..."
              className="rounded-xl border-[#EBE8E0]"
              data-testid="dog-name-input"
            />
          </div>

          {/* Breed */}
          <div>
            <label className="block text-sm font-medium text-[#1A1817] mb-2">Breed *</label>
            <select
              value={form.breed}
              onChange={e => setForm(p => ({ ...p, breed: e.target.value }))}
              className="w-full h-10 px-3 rounded-xl border border-[#EBE8E0] bg-white text-[#1A1817]"
              data-testid="dog-breed-select"
            >
              <option value="">Select breed...</option>
              {BREEDS.map(breed => (
                <option key={breed} value={breed}>{breed}</option>
              ))}
            </select>
          </div>

          {/* Age & Gender */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#1A1817] mb-2">Age (years)</label>
              <Input
                type="number"
                min="0"
                max="25"
                value={form.age}
                onChange={e => setForm(p => ({ ...p, age: e.target.value }))}
                placeholder="2"
                className="rounded-xl border-[#EBE8E0]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A1817] mb-2">Gender</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setForm(p => ({ ...p, gender: 'male' }))}
                  className={`flex-1 py-2 px-4 rounded-xl font-medium transition ${
                    form.gender === 'male' ? 'bg-[#4E876C] text-white' : 'bg-[#F5F3EE] text-[#7A7571]'
                  }`}
                >
                  Male
                </button>
                <button
                  type="button"
                  onClick={() => setForm(p => ({ ...p, gender: 'female' }))}
                  className={`flex-1 py-2 px-4 rounded-xl font-medium transition ${
                    form.gender === 'female' ? 'bg-[#FF7555] text-white' : 'bg-[#F5F3EE] text-[#7A7571]'
                  }`}
                >
                  Female
                </button>
              </div>
            </div>
          </div>

          {/* Looking For */}
          <div>
            <label className="block text-sm font-medium text-[#1A1817] mb-2">Looking For</label>
            <div className="flex flex-wrap gap-2">
              {['playdate', 'breeding', 'both'].map(option => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setForm(p => ({ ...p, looking_for: option }))}
                  className={`py-2 px-4 rounded-full font-medium capitalize transition ${
                    form.looking_for === option
                      ? 'bg-[#FF7555] text-white'
                      : 'bg-[#F5F3EE] text-[#7A7571] hover:bg-[#EBE8E0]'
                  }`}
                >
                  {option === 'both' ? 'Open to Both' : option}
                </button>
              ))}
            </div>
          </div>

          {/* Bio */}
          <div>
            <label className="block text-sm font-medium text-[#1A1817] mb-2">Bio</label>
            <textarea
              value={form.bio}
              onChange={e => setForm(p => ({ ...p, bio: e.target.value }))}
              placeholder="Tell us about your pup's personality..."
              className="w-full px-4 py-3 rounded-xl border border-[#EBE8E0] resize-none h-24"
              maxLength={300}
            />
            <p className="text-xs text-[#7A7571] mt-1">{form.bio.length}/300</p>
          </div>

          {/* Personality Traits */}
          <div>
            <label className="block text-sm font-medium text-[#1A1817] mb-2">Personality (pick up to 5)</label>
            <div className="flex flex-wrap gap-2">
              {PERSONALITIES.map(trait => (
                <button
                  key={trait}
                  type="button"
                  onClick={() => togglePersonality(trait)}
                  className={`py-1.5 px-3 rounded-full text-sm font-medium transition ${
                    form.personality.includes(trait)
                      ? 'bg-[#4E876C] text-white'
                      : 'bg-[#F5F3EE] text-[#7A7571] hover:bg-[#EBE8E0]'
                  }`}
                >
                  {trait}
                </button>
              ))}
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-[#1A1817] mb-2">Suburb/Area</label>
            <Input
              value={form.suburb}
              onChange={e => setForm(p => ({ ...p, suburb: e.target.value }))}
              placeholder="e.g., Sydney, Melbourne..."
              className="rounded-xl border-[#EBE8E0]"
            />
          </div>

          {/* Photo Upload */}
          <div>
            <label className="block text-sm font-medium text-[#1A1817] mb-2">
              Photos (up to 5)
            </label>
            <div className="grid grid-cols-5 gap-2">
              {form.photos.map((photo, idx) => (
                <div key={idx} className="relative aspect-square rounded-xl overflow-hidden group">
                  <img src={photo} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(idx)}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {form.photos.length < 5 && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="aspect-square rounded-xl border-2 border-dashed border-[#EBE8E0] hover:border-[#FF7555] flex items-center justify-center transition-colors"
                >
                  {uploading ? (
                    <Loader2 className="w-6 h-6 text-[#FF7555] animate-spin" />
                  ) : (
                    <Camera className="w-6 h-6 text-[#7A7571]" />
                  )}
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
              className="hidden"
              data-testid="photo-upload-input"
            />
            <p className="text-xs text-[#7A7571] mt-2">
              {uploading ? 'Uploading...' : 'Click to add photos of your pup'}
            </p>
          </div>

          {/* Vaccinated */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setForm(p => ({ ...p, vaccinated: !p.vaccinated }))}
              className={`w-6 h-6 rounded-lg flex items-center justify-center transition ${
                form.vaccinated ? 'bg-[#4E876C] text-white' : 'border-2 border-[#EBE8E0]'
              }`}
            >
              {form.vaccinated && <Check className="w-4 h-4" />}
            </button>
            <span className="text-sm text-[#1A1817]">Vaccinations up to date</span>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={saving}
            className="w-full h-12 bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl font-semibold"
            data-testid="save-dog-button"
          >
            {saving ? 'Saving...' : (editDog ? 'Update Profile' : 'Add Dog')}
          </Button>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Dog Card Component
const DogCard = ({ dog, onEdit, onDelete }) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-[#EBE8E0]"
    >
      <div className="relative h-48 bg-gradient-to-br from-[#FF7555]/20 to-[#F4D35E]/20">
        {dog.photos?.[0] ? (
          <img src={dog.photos[0]} alt={dog.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Dog className="w-16 h-16 text-[#FF7555]/40" />
          </div>
        )}
        {dog.vaccinated && (
          <div className="absolute top-3 right-3 bg-[#4E876C] text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <Shield className="w-3 h-3" /> Vaccinated
          </div>
        )}
      </div>
      
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="text-xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              {dog.name}, {dog.age}
            </h3>
            <p className="text-sm text-[#7A7571]">{dog.breed}</p>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            dog.looking_for === 'breeding' ? 'bg-[#FF7555]/10 text-[#FF7555]' :
            dog.looking_for === 'playdate' ? 'bg-[#F4D35E]/20 text-[#B8960A]' :
            'bg-[#9B6B9E]/10 text-[#9B6B9E]'
          }`}>
            {dog.looking_for === 'both' ? 'Open' : dog.looking_for}
          </span>
        </div>
        
        {dog.bio && (
          <p className="text-sm text-[#7A7571] mb-3 line-clamp-2">{dog.bio}</p>
        )}
        
        {dog.personality?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {dog.personality.slice(0, 3).map(trait => (
              <span key={trait} className="text-xs bg-[#F5F3EE] text-[#7A7571] px-2 py-1 rounded-full">
                {trait}
              </span>
            ))}
          </div>
        )}
        
        <div className="flex gap-2 pt-2 border-t border-[#EBE8E0]">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(dog)}
            className="flex-1 text-[#7A7571] hover:text-[#1A1817] hover:bg-[#F5F3EE]"
          >
            <Edit2 className="w-4 h-4 mr-1" /> Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(dog.id)}
            className="flex-1 text-red-500 hover:text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-1" /> Remove
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Main Profile Component
export default function PuppotoProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [user, setUser] = useState(location.state?.user || null);
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDog, setEditingDog] = useState(null);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (user) {
        setLoading(false);
        fetchDogs();
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/puppoto/auth/me`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          navigate('/puppoto', { replace: true });
          return;
        }
        
        const userData = await response.json();
        setUser(userData);
        fetchDogs();
      } catch (error) {
        navigate('/puppoto', { replace: true });
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const fetchDogs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/puppoto/my-dogs`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDogs(data.dogs || []);
      }
    } catch (error) {
      console.error('Failed to fetch dogs:', error);
    }
  };

  const handleSaveDog = async (dogData) => {
    const url = editingDog 
      ? `${API_URL}/api/puppoto/my-dogs/${editingDog.id}`
      : `${API_URL}/api/puppoto/my-dogs`;
    
    const method = editingDog ? 'PUT' : 'POST';

    const response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(dogData)
    });

    if (!response.ok) throw new Error('Failed to save');
    
    toast.success(editingDog ? 'Profile updated!' : 'Dog added!');
    setEditingDog(null);
    fetchDogs();
  };

  const handleDeleteDog = async (dogId) => {
    if (!confirm('Are you sure you want to remove this dog?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/puppoto/my-dogs/${dogId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Dog removed');
        fetchDogs();
      }
    } catch (error) {
      toast.error('Failed to remove dog');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/puppoto/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      // Continue with logout even if API fails
    }
    navigate('/puppoto', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-[#FF7555] to-[#E5684A] rounded-2xl" />
          <p className="text-[#7A7571]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Figtree', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#EBE8E0]">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/puppoto" className="flex items-center gap-2">
            <ChevronLeft className="w-5 h-5 text-[#7A7571]" />
          </a>
          
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-[#FF7555] to-[#E5684A] rounded-xl flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              My Profile
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="compact" />
            <button
              onClick={handleLogout}
              className="p-2 rounded-full hover:bg-[#F5F3EE] text-[#7A7571]"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* User Info */}
        <div className="bg-white rounded-2xl p-6 mb-8 shadow-[0_4px_20px_rgba(0,0,0,0.04)] border border-[#EBE8E0]">
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <div className="w-16 h-16 bg-[#FF7555]/10 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-[#FF7555]" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                {user?.name || 'Dog Lover'}
              </h1>
              <p className="text-[#7A7571]">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* My Dogs Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              My Dogs ({dogs.length})
            </h2>
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl"
              data-testid="add-dog-button"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Dog
            </Button>
          </div>

          {dogs.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white rounded-2xl p-8 text-center border border-dashed border-[#EBE8E0]"
            >
              <Dog className="w-16 h-16 text-[#FF7555]/30 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#1A1817] mb-2">No dogs yet!</h3>
              <p className="text-[#7A7571] mb-4">Add your first furry friend to start matching</p>
              <Button
                onClick={() => setShowAddModal(true)}
                className="bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Your First Dog
              </Button>
            </motion.div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {dogs.map(dog => (
                <DogCard
                  key={dog.id}
                  dog={dog}
                  onEdit={(d) => { setEditingDog(d); setShowAddModal(true); }}
                  onDelete={handleDeleteDog}
                />
              ))}
            </div>
          )}
        </div>

        {/* Start Matching CTA */}
        {dogs.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-[#FF7555] to-[#E5684A] rounded-2xl p-6 text-white text-center"
          >
            <Sparkles className="w-10 h-10 mx-auto mb-3" />
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Ready to Find Matches?
            </h3>
            <p className="text-white/80 mb-4">Start swiping to find playmates and breeding partners!</p>
            <Button
              onClick={() => navigate('/puppoto/match')}
              className="bg-white text-[#FF7555] hover:bg-white/90 rounded-xl font-semibold"
              data-testid="start-matching-button"
            >
              <Heart className="w-4 h-4 mr-2" /> Start Matching
            </Button>
          </motion.div>
        )}
      </main>

      {/* Add/Edit Dog Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddDogModal
            isOpen={showAddModal}
            onClose={() => { setShowAddModal(false); setEditingDog(null); }}
            onSave={handleSaveDog}
            editDog={editingDog}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
